import { computed, ref, type ComputedRef, type Ref } from 'vue'

export type SortDirection = 'asc' | 'desc'

type SortValue = string | number | null | undefined
type SortAccessors<T> = Record<string, (row: T) => SortValue>

interface UseTableSortOptions {
    /** When set, the active column + direction are persisted in localStorage under this key. */
    storageKey?: string
}

interface StoredSort {
    key: string
    direction: SortDirection
}

const compare = (a: SortValue, b: SortValue): number => {
    if (typeof a === 'number' && typeof b === 'number') {
        return a - b
    }

    return String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true, sensitivity: 'base' })
}

const readStoredSort = (storageKey: string, accessors: SortAccessors<unknown>): StoredSort | null => {
    try {
        const raw = localStorage.getItem(storageKey)
        if (!raw) {
            return null
        }

        const parsed = JSON.parse(raw)
        if (
            parsed
            && typeof parsed.key === 'string'
            && accessors[parsed.key]
            && (parsed.direction === 'asc' || parsed.direction === 'desc')
        ) {
            return { key: parsed.key, direction: parsed.direction }
        }
    } catch {
        // Ignore unavailable/corrupt storage and fall back to the default order.
    }

    return null
}

/**
 * Client-side column sorting for already-loaded table rows.
 *
 * `accessors` maps a column key to the value the column should sort on, so
 * derived columns (translated labels, joined strings, …) can sort on their
 * display value rather than the raw field. Pass `options.storageKey` to
 * remember the user's choice across reloads.
 */
export function useTableSort<T>(
    rows: Ref<T[]> | ComputedRef<T[]>,
    accessors: SortAccessors<T>,
    options: UseTableSortOptions = {},
) {
    const sortKey = ref<string | null>(null)
    const sortDirection = ref<SortDirection>('asc')

    if (options.storageKey) {
        const stored = readStoredSort(options.storageKey, accessors as SortAccessors<unknown>)
        if (stored) {
            sortKey.value = stored.key
            sortDirection.value = stored.direction
        }
    }

    const persist = (): void => {
        if (!options.storageKey) {
            return
        }

        try {
            if (sortKey.value) {
                localStorage.setItem(options.storageKey, JSON.stringify({ key: sortKey.value, direction: sortDirection.value }))
            } else {
                localStorage.removeItem(options.storageKey)
            }
        } catch {
            // Ignore storage failures (quota, private mode, …); sorting still works in-memory.
        }
    }

    // Cycle a column through ascending → descending → unsorted on repeated clicks.
    const toggleSort = (key: string): void => {
        if (sortKey.value !== key) {
            sortKey.value = key
            sortDirection.value = 'asc'
        } else if (sortDirection.value === 'asc') {
            sortDirection.value = 'desc'
        } else {
            sortKey.value = null
            sortDirection.value = 'asc'
        }

        persist()
    }

    const sorted = computed<T[]>(() => {
        const key = sortKey.value
        const accessor = key ? accessors[key] : undefined
        if (!accessor) {
            return rows.value
        }

        const factor = sortDirection.value === 'asc' ? 1 : -1

        return [...rows.value].sort((a, b) => compare(accessor(a), accessor(b)) * factor)
    })

    return { sortKey, sortDirection, toggleSort, sorted }
}
